import React, { Suspense, lazy, useCallback, useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import useMediaQuery from "@mui/material/useMediaQuery";
import CloseIcon from "@mui/icons-material/Close";
import { formwizApi } from "./formwizApi";
import { getDef } from "./defsCache";
import { renderEmrPdf } from "./emrPdf";
import { eventBus } from "../../utils/eventBus";
import { practiceFeatures } from "../../config/practice";
import RequestActions from "./RequestActions";
import { internalApi } from "../internal/internalApi";
import { sendWithPdf } from "../internal/staffPdf";
import { hasStaffFields } from "../internal/staffScope";
import { useStaffFormFill } from "../internal/launch";
import InternalActions, { LOCKED_EMR } from "../internal/InternalActions";
import {
  CHANNEL_SHORT,
  EMR_STATUS,
  emrStatusOf,
  REQUEST_STATUS,
  bandSeverity,
  errInfo,
  fmtDate,
  fmtDateTime,
  fmtScore,
  sevTone,
  statusInfo,
  titleOf,
} from "./staffUtils";
import "./staff.css";

// Step 1's read-only view (scores, bands, red flags) is a lazy chunk.
const FormView = lazy(() => import("../view/FormView"));

// SubmissionDetail -> the renderer's response shape.
export function buildResponse(detail) {
  return {
    formId: detail.form_id,
    formVersion: detail.form_version,
    lang: detail.language || "en",
    answers: detail.answers || {},
    scores: detail.scores || null,
    startedAt: detail.started_at,
    completedAt: detail.submitted_at,
    // tablet mode: "Completed on a staff device (handed over by X)" in the
    // view header and on the filed PDF
    ...(detail.completed_via && detail.completed_via.via === "kiosk"
      ? { completedVia: { via: "kiosk", by: detail.completed_via.handed_over_by || null } }
      : {}),
    // internal staff form: "Completed by staff: X"
    ...(detail.completed_via && detail.completed_via.via === "internal"
      ? { completedVia: { via: "staff", by: detail.completed_via.by || null } }
      : {}),
    // staff completed the staff-only sections: "Staff sections completed by X on DATE"
    ...(detail.staff_sections && detail.staff_sections.by
      ? { staffSections: { by: detail.staff_sections.by, at: detail.staff_sections.at || null } }
      : {}),
    // an amendment: the banner naming the document it replaces
    ...(detail.staff && detail.staff.amends
      ? { amendment: { documentId: detail.staff.amends.emr_document_id || null, filedAt: detail.staff.amends.emr_posted_at || null, reason: detail.staff.amends.reason || "" } }
      : {}),
  };
}

/*
 * Send to EMR. Staff file what they reviewed: the server answers 409
 * pdf_required until a STAFF-rendered PDF is stored (the patient's own upload
 * is never filed by staff, and may be missing), so this browser renders the
 * PDF from the server-held answers, uploads it and retries once.
 */
// The upload carries the staff answers' revision the PDF was rendered from
// (detail.staff.revision, 0 when staff never saved); 409 stale_revision (staff
// saved meanwhile) re-reads the submission once and renders again.
export async function sendToEmrWithPdf({ detail, def, onStep }) {
  return sendWithPdf({
    detail,
    def,
    onStep,
    toResponse: (d) => buildResponse(d),
    reload: async () => {
      const [fresh, staffInfo] = await Promise.all([formwizApi.submission(detail.id), internalApi.get(detail.id).catch(() => null)]);
      return { ...fresh, ...(staffInfo ? { staff: staffInfo.staff, staff_sections: staffInfo.staff_sections } : {}) };
    },
  });
}

function openBlob(blob, filename, win) {
  const url = URL.createObjectURL(blob);
  if (win && !win.closed) {
    win.location.href = url;
  } else {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

const SUMMARY_KEYS = [
  "status", "review_status", "reviewed_by", "reviewed_at", "review_note", "emr_status", "emr_document_id",
  "emr_error", "emr_posted_at", "has_pdf", "pdf_source", "flag_severity", "flags", "primary_score", "primary_band",
];

// The PDF staff see is the one that is (or will be) filed: the stored copy
// once it was filed or when staff rendered it, otherwise a fresh render from
// the server-held answers (a patient-rendered upload is never shown as the
// record: nothing ties its bytes to those answers).
export const showStoredPdf = (detail) =>
  !!(detail && detail.has_pdf && (detail.pdf_source === "staff" || detail.emr_status === "posted"));

// "Completed on a staff device (handed over by X)" for tablet-mode submissions.
export function completedViaText(detail) {
  const v = detail && detail.completed_via;
  if (!v || v.via !== "kiosk") return "";
  return v.handed_over_by ? `Completed on a staff device (handed over by ${v.handed_over_by})` : "Completed on a staff device";
}
const mergeSummary = (detail, summary) => {
  if (!summary || typeof summary !== "object") return detail;
  const next = { ...detail };
  SUMMARY_KEYS.forEach((k) => {
    if (k in summary) next[k] = summary[k];
  });
  return next;
};

/*
 * One submission: Step 1 FormView (EN/ES toggle, score bands, red flags) and
 * a rail with the patient, the request, review and EMR status + actions.
 *   submissionId, onClose(), onChanged(summary)
 */
export default function SubmissionReview({ submissionId, onClose, onChanged }) {
  const narrow = useMediaQuery("(max-width: 900px)");
  const [detail, setDetail] = useState(null);
  const [def, setDef] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [viewLang, setViewLang] = useState("en");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  const [stepText, setStepText] = useState("");
  const [actionError, setActionError] = useState("");
  const [actionOk, setActionOk] = useState("");
  // Staff meta (GET /internal/submissions/{id}: staff, staff_sections) for
  // staff forms and forms with staff-only sections; false when unavailable.
  const [staffInfo, setStaffInfo] = useState(null);
  const [openStaffForm, staffFormFill] = useStaffFormFill();

  const load = useCallback(async () => {
    if (!submissionId) return;
    setLoadError("");
    try {
      const d = await formwizApi.submission(submissionId);
      setDetail(d);
      let df = null;
      try {
        df = await getDef(d.form_id, d.form_version);
        setDef(df);
      } catch (_) {
        setLoadError("Couldn't load the form definition for this submission.");
      }
      const internal = d.request && d.request.channel === "internal";
      if (practiceFeatures().staffForms && (internal || (df && d.status === "submitted" && hasStaffFields(df)))) {
        try {
          setStaffInfo(await internalApi.get(submissionId));
        } catch (_) {
          setStaffInfo(false);
        }
      }
    } catch (e) {
      const info = errInfo(e);
      setLoadError(
        info.status === 404 ? "This submission wasn't found." : info.status === 403 ? "You don't have access to this patient." : "Couldn't load the submission. Try again.",
      );
    }
  }, [submissionId]);

  useEffect(() => {
    setDetail(null);
    setDef(null);
    setStaffInfo(null);
    setViewLang("en");
    setActionError("");
    setActionOk("");
    load();
  }, [load]);

  // The detail with the staff meta merged in (revision, staff sections, amends).
  const staffMeta = staffInfo && staffInfo.staff ? staffInfo.staff : null;
  const fullDetail = detail && staffInfo ? { ...detail, staff: staffMeta, staff_sections: staffInfo.staff_sections || null, completed_via: staffInfo.completed_via || detail.completed_via } : detail;
  const isInternal = !!(detail && detail.request && detail.request.channel === "internal");
  const staffSections = staffInfo ? staffInfo.staff_sections : null;
  const openSections = () => openStaffForm(null, { submissionId: detail.id, mode: "staff_sections", startEditing: true, onDone: () => { load(); if (onChanged) onChanged(); } });
  const openInternal = (id, opts = {}) => openStaffForm(null, { submissionId: id || detail.id, startEditing: !!opts.startEditing, onDone: () => { load(); if (onChanged) onChanged(); } });

  const changed = (summary) => {
    setDetail((d) => mergeSummary(d, summary));
    eventBus.emit("formwiz:inbox-changed");
    if (onChanged) onChanged(summary);
  };

  const markReviewed = async () => {
    setBusy("review");
    setActionError("");
    setActionOk("");
    try {
      const summary = await formwizApi.review(detail.id, note.trim() || undefined);
      changed({ review_status: "reviewed", review_note: note.trim() || null, ...(summary || {}) });
      setNote("");
      setActionOk("Marked as reviewed.");
    } catch (e) {
      setActionError(errInfo(e).message || "Couldn't mark it reviewed. Try again.");
    } finally {
      setBusy("");
    }
  };

  const sendToEmr = async () => {
    if (!def) return;
    setBusy("emr");
    setActionError("");
    setActionOk("");
    setStepText("Sending to the EMR…");
    try {
      const summary = await sendToEmrWithPdf({ detail: fullDetail, def, onStep: setStepText });
      changed(summary);
      setActionOk(summary && summary.emr_document_id ? `Filed in the EMR (document ${summary.emr_document_id}).` : "Sent to the EMR.");
    } catch (e) {
      const info = errInfo(e);
      if (info.status === 409 && info.code === "already_posted") {
        setActionOk("Already in the EMR.");
      } else {
        const emrError = info.data && info.data.emr_error;
        setActionError(emrError || info.message || "Couldn't send to the EMR. Try again.");
      }
      // The row's EMR status changed on the server either way: re-read it.
      try {
        const fresh = await formwizApi.submission(detail.id);
        setDetail((d) => mergeSummary(d, fresh));
        if (onChanged) onChanged(fresh);
        eventBus.emit("formwiz:inbox-changed");
      } catch (_) {
        /* keep what we have */
      }
    } finally {
      setBusy("");
      setStepText("");
    }
  };

  // Stored PDF when there is one; otherwise rendered here (the EMR version).
  const openPdf = async () => {
    if (!detail || !def) return;
    setBusy("pdf");
    setActionError("");
    let win = null;
    try {
      win = window.open("", "_blank");
    } catch (_) {
      win = null;
    }
    try {
      let blob = null;
      if (showStoredPdf(detail)) {
        try {
          const b = await formwizApi.submissionPdf(detail.id);
          if (typeof Blob !== "undefined" && b instanceof Blob && b.size > 0) blob = b;
        } catch (_) {
          blob = null;
        }
      }
      if (!blob) blob = await renderEmrPdf(def, buildResponse(fullDetail), detail.prefill || {});
      const typed = blob.type === "application/pdf" ? blob : new Blob([blob], { type: "application/pdf" });
      openBlob(typed, `${detail.form_id}-${detail.id}.pdf`, win);
    } catch (_) {
      if (win && !win.closed) win.close();
      setActionError("Couldn't open the PDF.");
    } finally {
      setBusy("");
    }
  };

  const prefill = (detail && detail.prefill) || {};
  const pt = prefill.patient || {};
  const patientName = [pt.firstName, pt.lastName].filter(Boolean).join(" ") || pt.fullName || "";
  const req = (detail && detail.request) || null;
  const [reqLabel, reqTone] = statusInfo(REQUEST_STATUS, req && req.status);
  const [emrLabel, emrTone] = statusInfo(EMR_STATUS, emrStatusOf(detail));
  const flags = (detail && Array.isArray(detail.flags) ? detail.flags : []) || [];
  const bandSev = def && detail ? bandSeverity(def, detail.primary_score, detail.primary_band) : null;
  const posted = detail && (detail.emr_status === "posted" || detail.emr_status === "posting");
  const reviewed = detail && detail.review_status === "reviewed";
  const viaText = completedViaText(detail);

  return (
    <Dialog open={!!submissionId} onClose={onClose} fullWidth maxWidth="xl" fullScreen={narrow}
      aria-labelledby="fws-review-title" PaperProps={{ sx: { height: narrow ? "100%" : "92vh" } }}>
      <div className="fws fws-dialog" data-testid="submission-review">
        <div className="fws-head">
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 id="fws-review-title">{detail ? detail.form_title || (def && titleOf(def.title)) || detail.form_id : "Submission"}</h2>
            {detail && (
              <div className="fws-head-sub">
                {patientName}{pt.dob ? ` · DOB ${fmtDate(pt.dob)}` : ""} · submitted {fmtDateTime(detail.submitted_at)}
              </div>
            )}
            {viaText && (
              <div className="fws-head-sub" data-testid="fws-completed-via">
                <span className="fws-chip fws-tone-warn">{viaText}</span>
              </div>
            )}
          </div>
          <button type="button" className="fws-icon-btn" aria-label="Close" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        {loadError && !detail && <div className="fws-body"><div className="fws-alert fws-alert-error" role="alert">{loadError}</div></div>}
        {!loadError && !detail && <div className="fws-body"><div className="fws-muted">Loading…</div></div>}

        {detail && (
          <div className="fws-split" style={{ flex: 1, minHeight: 0 }}>
            <div className="fws-split-main">
              {loadError && <div className="fws-alert fws-alert-error" style={{ margin: 12 }}>{loadError}</div>}
              {def && (
                <Suspense fallback={<div className="fws-muted" style={{ padding: 16 }}>Loading…</div>}>
                  <FormView
                    definition={def}
                    response={buildResponse(fullDetail)}
                    prefill={prefill}
                    lang={viewLang}
                    onLangChange={setViewLang}
                    onPdf={openPdf}
                  />
                </Suspense>
              )}
            </div>

            <aside className="fws-rail" aria-label="Review">
              <div className="fws-rail-block">
                <div className="fws-rail-title">Patient</div>
                <dl className="fws-kv">
                  <dt>Name</dt><dd>{patientName || "—"}</dd>
                  <dt>DOB</dt><dd>{fmtDate(pt.dob) || "—"}</dd>
                  <dt>Patient ID</dt><dd>{pt.mrn || detail.patientid || (detail.request && detail.request.patientid) || "—"}</dd>
                  {prefill.appointment && prefill.appointment.date && (<><dt>Visit</dt><dd>{fmtDate(prefill.appointment.date)}{prefill.appointment.provider ? ` · ${prefill.appointment.provider}` : ""}</dd></>)}
                </dl>
              </div>

              {(flags.length > 0 || (detail.primary_score !== null && detail.primary_score !== undefined)) && (
                <div className="fws-rail-block">
                  <div className="fws-rail-title">Result</div>
                  {detail.primary_score !== null && detail.primary_score !== undefined && (
                    <div className="fws-row">
                      <span className="fws-strong">Score {fmtScore(detail.primary_score)}</span>
                      {detail.primary_band && <span className={`fws-chip has-dot fws-tone-${sevTone(bandSev)}`}>{detail.primary_band}</span>}
                    </div>
                  )}
                  {flags.map((f) => (
                    <div key={f.id || f.label} className={`fws-flag-row fws-tone-${sevTone(f.severity)}`} role="alert">
                      <b>!</b><span>{titleOf(f.label)}</span>
                    </div>
                  ))}
                </div>
              )}

              {isInternal && (
                <div className="fws-rail-block" data-testid="fws-staff-form-block">
                  <div className="fws-rail-title">Staff form</div>
                  <dl className="fws-kv">
                    <dt>Filled by</dt><dd>{(staffMeta && (staffMeta.finalized_by || staffMeta.created_by)) || (req && req.created_by) || "—"}</dd>
                    {staffMeta && staffMeta.revision > 0 && (<><dt>Revision</dt><dd>{staffMeta.revision}{staffMeta.edited_by ? ` · ${staffMeta.edited_by}` : ""}{staffMeta.edited_at ? ` · ${fmtDateTime(staffMeta.edited_at)}` : ""}</dd></>)}
                    {staffMeta && staffMeta.kind === "amendment" && staffMeta.amends && (<><dt>Amends</dt><dd>Document {staffMeta.amends.emr_document_id || "—"}{staffMeta.amends.reason ? ` · ${staffMeta.amends.reason}` : ""}</dd></>)}
                    <dt>Language</dt><dd>{(detail.language || "en") === "es" ? "Español" : "English"}</dd>
                  </dl>
                  {req && req.note && <p className="fws-small fws-muted" style={{ marginTop: 6 }}>Note: {req.note}</p>}
                </div>
              )}

              {req && !isInternal && (
                <div className="fws-rail-block">
                  <div className="fws-rail-title">Request</div>
                  <dl className="fws-kv">
                    <dt>Status</dt><dd><span className={`fws-chip fws-tone-${reqTone}`}>{reqLabel}</span></dd>
                    <dt>Sent by</dt><dd>{req.created_by || "—"}</dd>
                    <dt>Channel</dt><dd>{CHANNEL_SHORT[req.channel] || req.channel}{req.destination_masked ? ` · ${req.destination_masked}` : ""}</dd>
                    <dt>Sent</dt><dd>{fmtDateTime(req.last_sent_at || req.created_at) || "—"}</dd>
                    <dt>Language</dt><dd>{(detail.language || req.language || "en") === "es" ? "Español" : "English"}</dd>
                    {viaText && (<><dt>Completed on</dt><dd>{viaText.replace(/^Completed on a /, "A ")}</dd></>)}
                  </dl>
                  {req.note && <p className="fws-small fws-muted" style={{ marginTop: 6 }}>Note: {req.note}</p>}
                  <RequestActions
                    request={req}
                    patientName={patientName}
                    onChanged={(r) => {
                      if (r && r.id) setDetail((d) => ({ ...d, request: { ...d.request, ...r } }));
                    }}
                  />
                </div>
              )}

              {!isInternal && staffInfo && staffMeta && def && hasStaffFields(def) && (
                <div className="fws-rail-block" data-testid="fws-staff-sections">
                  <div className="fws-rail-title">Staff sections</div>
                  <p className="fws-small" data-testid="fws-staff-sections-line">
                    {staffSections && staffSections.by
                      ? `Completed by ${staffSections.by}${staffSections.at ? ` on ${fmtDate(staffSections.at) || fmtDateTime(staffSections.at)}` : ""}${staffSections.revision || staffMeta.revision ? ` (rev ${staffSections.revision || staffMeta.revision})` : ""}`
                      : "Not completed"}
                    {staffMeta.pending_edit ? <span className="fws-chip fws-tone-warn" style={{ marginLeft: 6 }}>Unsaved edit</span> : null}
                  </p>
                  {staffMeta.editable && (
                    <button type="button" className="fws-btn fws-btn-primary fws-btn-sm" style={{ marginTop: 8 }} onClick={openSections}>
                      {staffSections && staffSections.by ? "Edit staff sections" : "Complete staff sections"}
                    </button>
                  )}
                  {(staffMeta.can_amend || staffMeta.pending_edit) && (
                    <div style={{ marginTop: 8 }}>
                      <InternalActions detail={fullDetail} def={def} showEmr={false} showPrint={false}
                        onChanged={(fresh) => { if (fresh) setStaffInfo(fresh); }}
                        onOpenSubmission={(id, opts) => openInternal(id, opts)} />
                    </div>
                  )}
                </div>
              )}

              {isInternal ? (
                <div className="fws-rail-block">
                  <div className="fws-rail-title">Review</div>
                  <p className="fws-small" data-testid="fws-reviewed">
                    <span className="fws-chip fws-tone-ok">Staff form</span>{" "}
                    {detail.reviewed_by ? `saved by ${detail.reviewed_by}` : ""}{detail.reviewed_at ? ` · ${fmtDateTime(detail.reviewed_at)}` : ""}
                  </p>
                </div>
              ) : (
              <div className="fws-rail-block">
                <div className="fws-rail-title">Review</div>
                {reviewed ? (
                  <p className="fws-small" data-testid="fws-reviewed">
                    <span className="fws-chip fws-tone-ok">Reviewed</span>{" "}
                    {detail.reviewed_by ? `by ${detail.reviewed_by}` : ""}{detail.reviewed_at ? ` · ${fmtDateTime(detail.reviewed_at)}` : ""}
                    {detail.review_note && <span style={{ display: "block", marginTop: 4 }}>“{detail.review_note}”</span>}
                  </p>
                ) : (
                  <div className="fws-stack">
                    <textarea className="fws-textarea" aria-label="Review note" placeholder="Note (optional)" maxLength={1000}
                      value={note} onChange={(e) => setNote(e.target.value)} />
                    <button type="button" className="fws-btn fws-btn-primary" disabled={!!busy} onClick={markReviewed}>
                      {busy === "review" ? "Saving…" : "Mark reviewed"}
                    </button>
                  </div>
                )}
              </div>
              )}

              <div className="fws-rail-block">
                <div className="fws-rail-title">EMR</div>
                <div className="fws-row">
                  <span className={`fws-chip fws-tone-${emrTone}`} data-testid="fws-emr-status">{emrLabel}</span>
                  {detail.emr_document_id && <span className="fws-small fws-muted">Document {detail.emr_document_id}</span>}
                </div>
                {detail.emr_status === "failed" && detail.emr_error && (
                  <p className="fws-err" style={{ marginTop: 6 }}>{detail.emr_error}</p>
                )}
                {detail.emr_status === "skipped" && detail.emr_error && (
                  <p className="fws-small fws-muted" style={{ marginTop: 6 }}>{detail.emr_error}</p>
                )}
                {isInternal && staffMeta ? (
                  <div style={{ marginTop: 8 }}>
                    <InternalActions
                      detail={fullDetail}
                      def={def}
                      onChanged={(fresh) => {
                        if (fresh) {
                          setStaffInfo(fresh);
                          setDetail((d) => mergeSummary(d, fresh));
                          if (onChanged) onChanged(fresh);
                        }
                      }}
                      onEdit={() => openInternal(detail.id, { startEditing: true })}
                      onOpenSubmission={(id, opts) => openInternal(id, opts)}
                    />
                  </div>
                ) : (
                <div className="fws-row" style={{ marginTop: 8 }}>
                  <button type="button" className="fws-btn fws-btn-accent" disabled={!!busy || posted || (isInternal && LOCKED_EMR.includes(detail.emr_status)) || !def} onClick={sendToEmr}>
                    {busy === "emr" ? stepText || "Sending…" : detail.emr_status === "failed" ? "Retry Send to EMR" : "Send to EMR"}
                  </button>
                  <button type="button" className="fws-btn" disabled={!!busy || !def} onClick={openPdf}>
                    {busy === "pdf" ? "Opening…" : "PDF"}
                  </button>
                </div>
                )}
              </div>

              {actionError && <div className="fws-alert fws-alert-error" role="alert">{actionError}</div>}
              {actionOk && <div className="fws-alert fws-alert-ok" role="status">{actionOk}</div>}
            </aside>
          </div>
        )}
      </div>
      {staffFormFill}
    </Dialog>
  );
}
